import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Permission, useCan } from "@/components/session-provider";
import { PersonDetailPanel } from "@/components/admin/PersonDetailPanel";
import { PersonTickets } from "@/components/admin/PersonTickets";
import { getPerson, type Person } from "@/lib/people";

export const Route = createFileRoute("/people/$id")({
  component: PersonPage,
});

/**
 * One person, as a destination of their own.
 *
 * The roster's inline panel stays where it is — mid-ticket, an agent wants a phone number
 * without losing the list they were reading. This is for the other half of the job: a
 * colleague's record you were sent to, want to keep open in a tab, or want to send to
 * somebody else. A URL is what makes those possible and an expanded row cannot.
 *
 * ⚠️ The permission gate here decides what to RENDER and nothing else. Reaching the page
 * needs `user.manage`, the endpoint behind it re-checks that server-side, and every field
 * on the panel is disabled unless the server said this caller may administer this person
 * — `canAdminister` folds their permissions, scopes and clearances against the subject's,
 * which the browser has no way to work out. Deriving any of it here would be defect D1.
 *
 * ⚠️ A 404 is answered as "not found", deliberately without distinguishing "no such
 * person" from "not yours to see". That is the same no-enumeration-oracle rule the ticket
 * endpoints follow, and saying "this person exists but you may not see them" would defeat
 * it from the client side.
 */
function PersonPage() {
  const { id } = Route.useParams();
  const canManageUsers = useCan(Permission.UserManage);

  const [person, setPerson] = useState<Person | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPerson(await getPerson(id));
      setError(null);
    } catch (e) {
      setPerson(null);
      setError(
        e instanceof ApiError && e.status === 404
          ? "That person was not found."
          : "That person could not be loaded.",
      );
    }
  }, [id]);

  useEffect(() => {
    if (!canManageUsers) return;
    void load();
  }, [canManageUsers, load]);

  if (!canManageUsers) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
        <p className="text-sm text-muted-foreground">
          You do not have access to the staff directory.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      {/* One breadcrumb rather than a bare back arrow. Somebody reaches this page from the
          roster, from a ticket's requester, or from a pasted link, and "back" means three
          different things in those three cases — a trail says where you are instead of
          guessing where you came from. */}
      <nav aria-label="Breadcrumb">
        <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <li>
            <Link
              to="/people"
              className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 transition hover:bg-accent hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              People
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="min-w-0 truncate font-medium text-foreground" aria-current="page">
            {person?.displayName ?? "…"}
          </li>
        </ol>
      </nav>

      <div className="mt-4">
        {error && (
          <p
            role="alert"
            className="rounded-2xl border border-dashed border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground"
          >
            {error}
          </p>
        )}

        {!error && person === null && (
          <div
            className="h-64 animate-pulse rounded-2xl border border-border bg-card"
            aria-busy="true"
          />
        )}

        {person !== null && (
          // Their details beside their requests: the two questions somebody opens a
          // colleague's record to answer, rather than one of them and a link to the other.
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
            {/* No onClose: the way out of a page is the browser's own back, and an X that
                navigated somewhere would be a second, worse one. */}
            <PersonDetailPanel person={person} onChanged={() => void load()} />

            <PersonTickets userId={person.id} displayName={person.displayName} />
          </div>
        )}
      </div>
    </div>
  );
}
